import React, { useRef, useState, useMemo } from 'react';
import { toPng } from 'html-to-image';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Download, Copy, Image as ImageIcon, Loader2, Share2, Check, Smartphone, AtSign } from 'lucide-react';
import SharePoster, { POSTER_THEME_LIST, POSTER_THEMES } from './SharePoster';
import { SPRITES } from '../mock';
import { prefetchAll } from '../lib/posterImages';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

export default function ShareImageButton({ collection, sprites = SPRITES, filterStatus = 'all', season = 'all' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [profileCopied, setProfileCopied] = useState(false);
  const [themeId, setThemeId] = useState('llama');
  const posterRef = useRef(null);
  const [dataUrlMap, setDataUrlMap] = useState(null);
  const [headerDataUrl, setHeaderDataUrl] = useState(null);

  const { user, isAuthed } = useAuth();
  const profileUrl = isAuthed && user?.username && typeof window !== 'undefined'
    ? `${window.location.origin}/u/${user.username}`
    : null;

  const isMobile = typeof navigator !== 'undefined' &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
  const canWebShareFiles = typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function';

  // Display-only short URL (the live preview URL is the canonical one)
  const liveUrl = typeof window !== 'undefined' ? window.location.origin : 'https://spritevault.app';
  const shortUrl = useMemo(() => {
    try {
      const u = new URL(liveUrl);
      return u.host;
    } catch {
      return 'spritevault.app';
    }
  }, [liveUrl]);

  // Wait until every <img> inside the poster is fully decoded — mobile Safari
  // otherwise captures the frame while sprites are still blank.
  const waitForImagesReady = async () => {
    if (!posterRef.current) return;
    const imgs = Array.from(posterRef.current.querySelectorAll('img'));
    await Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => { img.onload = null; img.onerror = null; resolve(); };
        img.onload = done;
        img.onerror = done;
        // safety net so a stuck image can't hang the export
        setTimeout(done, 4000);
      });
    }));
    // give layout one more paint frame
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 120)));
  };

  const generate = async () => {
    setLoading(true);
    setProgress(0);
    setPreviewUrl(null);
    try {
      // 0) make sure our display fonts (Bowlby One SC etc.) are ready — otherwise
      //    the first toPng pass captures Impact/system fallback and the Fortnite
      //    logo look is lost. document.fonts.ready resolves once all @font-face
      //    declarations referenced on the page have finished loading.
      try {
        if (document.fonts && typeof document.fonts.ready?.then === 'function') {
          // Force-load the display fonts explicitly so they're guaranteed to be
          // in the font-set before we rasterise (document.fonts.ready alone
          // waits only for fonts already requested — a hidden off-screen SVG
          // may not have triggered the load yet).
          try {
            await Promise.all([
              document.fonts.load('900 200px "Burbank Big Condensed Black"'),
              document.fonts.load('900 100px "Burbank Big Condensed Black"'),
              document.fonts.load('bold 48px "Fjalla One"'),
              document.fonts.load('900 40px "Rubik"'),
            ]);
          } catch { /* ignore */ }
          await document.fonts.ready;
        }
      } catch { /* older browsers — best effort */ }

      // 1) prefetch all sprite images as data URLs (CORS-safe via proxy)
      const urls = Array.from(new Set(sprites.map((s) => s.img)));
      const map = await prefetchAll(urls, (done, total) => {
        setProgress(Math.round((done / total) * 55));
      });
      setDataUrlMap(map);

      // 1b) Preload the AI-generated header PNG as a data URL too. Mobile
      //     Safari sometimes fails to embed same-origin images that arrive
      //     mid-render — inlining as data URL removes that race entirely.
      try {
        const headerRes = await fetch('/branding/poster-header.png', { cache: 'force-cache' });
        const headerBlob = await headerRes.blob();
        const headerData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(headerBlob);
        });
        setHeaderDataUrl(headerData);
      } catch (e) {
        // fallback: pass the raw path; SharePoster will still try to render it
        setHeaderDataUrl(null);
      }

      // Let React render the new data URLs into the off-screen poster
      await new Promise((r) => setTimeout(r, 300));
      setProgress(70);

      // 2) wait until every <img> is decoded and painted
      await waitForImagesReady();
      setProgress(85);

      if (!posterRef.current) throw new Error('Poster not ready');

      // 3) Rasterise. Mobile Safari sometimes returns a blank/partial PNG on
      //    the first pass — running toPng multiple times is a well-known fix
      //    (each pass picks up any newly-decoded remote images). Three passes
      //    on mobile is the sweet spot; two passes on desktop is enough.
      const opts = {
        cacheBust: true,
        pixelRatio: isMobile ? 2.5 : 4,
        backgroundColor: '#02000a',
      };
      const passes = isMobile ? 3 : 2;
      let png = null;
      for (let i = 0; i < passes; i++) {
        png = await toPng(posterRef.current, opts);
      }
      setProgress(100);
      setPreviewUrl(png);
    } catch (e) {
      console.error(e);
      toast({ title: 'Could not generate image', description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `sprite-vault-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast({ title: 'PNG saved', description: 'Your sprite poster has been downloaded.' });
  };

  const copyImage = async () => {
    if (!previewUrl) return;
    try {
      const blob = await (await fetch(previewUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      toast({ title: 'Image copied!', description: 'Paste it into Messenger, Discord, or anywhere.' });
    } catch (e) {
      toast({ title: 'Copy failed', description: 'Use Download instead and attach the file.' });
    }
  };

  const copyLink = async () => {
    // Prefer the signed-in user's public profile link so pasting it beside
    // the poster leads recipients straight to that user's trade page.
    const target = profileUrl || liveUrl;
    try {
      await navigator.clipboard.writeText(target);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
      toast({
        title: profileUrl ? 'Profile link copied!' : 'Link copied!',
        description: profileUrl
          ? `Paste it beside the poster — friends open your Sprite Vault page directly.`
          : 'Paste it next to the image so people can open the live site.',
      });
    } catch (e) {
      toast({ title: 'Copy failed' });
    }
  };

  const copyProfile = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setProfileCopied(true);
      setTimeout(() => setProfileCopied(false), 1800);
      toast({
        title: 'Profile link copied!',
        description: `Paste it with the poster — anyone who taps it lands on your trade page.`,
      });
    } catch (e) {
      toast({ title: 'Copy failed', description: 'Long-press and copy manually.' });
    }
  };

  const shareNative = async () => {
    if (!previewUrl) return;
    try {
      const blob = await (await fetch(previewUrl)).blob();
      const file = new File([blob], 'sprite-vault.png', { type: 'image/png' });
      const shareUrl = profileUrl || liveUrl;
      const shareText = profileUrl
        ? `Check out my Fortnite Sprite collection! Trade with me @${user.username} — ${profileUrl}`
        : `Check out my Fortnite Sprite collection! ${liveUrl}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Sprite Vault',
          text: shareText,
          url: shareUrl,
        });
      } else if (navigator.share) {
        await navigator.share({
          title: 'My Sprite Vault',
          text: shareText,
          url: shareUrl,
        });
      } else {
        toast({ title: 'Sharing not supported', description: 'Download or copy the image instead.' });
      }
    } catch (e) {
      // user cancelled or unsupported
    }
  };

  // Save to Photos / Camera Roll on mobile via the native share sheet.
  // On iOS Safari the share sheet exposes a "Save Image" action which stores
  // the PNG directly to the Photos app. On Android the same sheet exposes
  // "Save to device" / "Photos". Falls back to opening the image so the user
  // can long-press → Save Image.
  const saveToPhotos = async () => {
    if (!previewUrl) return;
    try {
      const blob = await (await fetch(previewUrl)).blob();
      const file = new File([blob], `sprite-vault-${Date.now()}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Sprite Vault',
        });
        toast({ title: 'Tap "Save Image" in the share sheet', description: 'It will save straight to your Photos.' });
        return;
      }
      // Fallback: open the image in a new tab so the user can long-press → Save
      const win = window.open();
      if (win) {
        win.document.write(`
          <html><head><title>Sprite Vault poster</title>
          <meta name="viewport" content="width=device-width,initial-scale=1">
          <style>body{margin:0;background:#3a1064;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;padding:16px;font-family:system-ui,sans-serif;color:#ffd900}p{font-weight:900;margin:12px 0}</style>
          </head><body>
            <p>Long-press the image → Save to Photos</p>
            <img src="${previewUrl}" style="max-width:100%;height:auto;border-radius:12px" />
          </body></html>
        `);
        win.document.close();
      } else {
        // Very last fallback — trigger a download
        download();
      }
    } catch (e) {
      // user cancelled
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-10 rounded-xl border-2 font-black shadow-[0_3px_0_var(--t-card-border)] hover:opacity-90"
        style={{
          background: '#ffd900',
          color: '#3a1064',
          borderColor: 'var(--t-card-border)',
        }}
        onClick={() => { setOpen(true); setPreviewUrl(null); setTimeout(generate, 100); }}
      >
        <ImageIcon className="h-4 w-4 mr-2" /> Generate Poster
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[760px] w-[calc(100vw-16px)] max-h-[92vh] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_8px_0_#3a1064] p-0 overflow-y-auto">
          <div className="p-5 border-b-[3px] border-[#3a1064] bg-[#ffd900]">
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="text-[#3a1064] font-black text-2xl flex items-center gap-2">
                <ImageIcon className="h-6 w-6" /> Share your Sprite Vault
              </DialogTitle>
              <DialogDescription className="text-[#3a1064]/80 font-bold">
                Download a PNG of your collection. Drop it in Facebook Messenger, Discord, or anywhere — and paste the link too so friends can open the live site to trade.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-5 bg-[#fff7d6] max-h-[60vh] overflow-y-auto">
            {/* Theme picker — 5 preset color palettes for the poster */}
            <div className="mb-4" data-testid="poster-theme-picker">
              <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70 mb-2">
                Poster theme
              </div>
              <div className="flex flex-wrap gap-2">
                {POSTER_THEME_LIST.map((t) => {
                  const active = t.id === themeId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`poster-theme-${t.id}`}
                      onClick={() => {
                        if (t.id === themeId || loading) return;
                        setThemeId(t.id);
                        setPreviewUrl(null);
                        setTimeout(generate, 100);
                      }}
                      className={`flex items-center gap-2 rounded-xl border-[3px] px-2.5 py-2 font-black text-xs transition-transform hover:-translate-y-0.5 active:translate-y-0 ${
                        active
                          ? 'bg-[#3a1064] text-white shadow-[0_3px_0_#1a0440]'
                          : 'bg-white text-[#3a1064] border-[#3a1064]/60 hover:border-[#3a1064]'
                      }`}
                      style={active ? { borderColor: '#1a0440' } : undefined}
                      aria-pressed={active}
                    >
                      <span className="flex items-center gap-0.5">
                        {t.swatches.map((c) => (
                          <span
                            key={c}
                            className="h-4 w-4 rounded-sm border border-black/40"
                            style={{ background: c }}
                          />
                        ))}
                      </span>
                      <span className="uppercase tracking-wider">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <Loader2 className="h-10 w-10 text-[#ff2bd6] animate-spin" />
                <div className="font-black text-[#3a1064]">Generating your poster... {progress}%</div>
                <div className="w-full max-w-sm h-2 bg-[#3a1064]/15 rounded-full overflow-hidden">
                  <div className="h-full bg-[#ff2bd6] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            {!loading && previewUrl && (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={previewUrl}
                  alt="Sprite Vault poster preview"
                  className="max-w-full rounded-xl border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064]"
                />
                {isMobile ? (
                  <div className="text-xs font-black text-[#3a1064] text-center max-w-md bg-yellow-100 border-2 border-[#ffd900] rounded-lg px-3 py-2">
                    📸 Tip: Tap <span className="text-[#ff2bd6]">Save to Photos</span> below, or long-press the image → <em>Save to Photos</em>.
                  </div>
                ) : (
                  <div className="text-xs font-bold text-[#3a1064]/80 text-center max-w-md">
                    Tip: in Messenger or Discord, attach the PNG <em>and</em> paste the link — the image preview becomes clickable.
                  </div>
                )}

                {profileUrl && (
                  <div
                    data-testid="poster-profile-link-box"
                    className="mt-1 w-full max-w-md rounded-xl border-[3px] border-[#3a1064] bg-yellow-50 p-2.5 sm:p-3 shadow-[0_3px_0_#3a1064]"
                  >
                    <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70 mb-1 flex items-center gap-1">
                      <AtSign className="h-3 w-3" /> Your public trade link
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div
                        className="flex-1 min-w-0 truncate font-black text-xs sm:text-sm text-[#3a1064] bg-white border-2 border-[#3a1064]/25 rounded-lg px-2 py-1.5"
                        title={profileUrl}
                      >
                        {profileUrl}
                      </div>
                      <Button
                        data-testid="poster-copy-profile-btn"
                        onClick={copyProfile}
                        className={`h-9 px-3 rounded-lg border-2 border-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064] shrink-0 ${
                          profileCopied ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-white hover:bg-yellow-100 text-[#3a1064]'
                        }`}
                      >
                        {profileCopied
                          ? <><Check className="h-3.5 w-3.5 mr-1" strokeWidth={3} /> Copied</>
                          : <><Copy className="h-3.5 w-3.5 mr-1" strokeWidth={3} /> Copy profile</>}
                      </Button>
                    </div>
                    <div className="mt-1.5 text-[10px] font-bold text-[#3a1064]/70 leading-tight">
                      Paste this next to the poster — anyone who taps it lands on your one-tap <span className="text-[#ff2bd6]">Trade with @{user?.username}</span> page.
                    </div>
                  </div>
                )}
              </div>
            )}
            {!loading && !previewUrl && (
              <div className="text-center py-10 text-[#3a1064] font-bold">Preparing image...</div>
            )}
          </div>

          <DialogFooter className="p-5 pt-0 bg-[#fff7d6] gap-2 flex-wrap">
            {isMobile && (
              <Button
                data-testid="save-to-photos-btn"
                className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064] w-full sm:w-auto"
                disabled={!previewUrl}
                onClick={saveToPhotos}
              >
                <Smartphone className="h-4 w-4 mr-2" /> Save to Photos
              </Button>
            )}
            <Button
              data-testid="poster-copy-link-btn"
              variant="outline"
              className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
              onClick={copyLink}
            >
              {linkCopied ? <Check className="h-4 w-4 mr-2 text-emerald-600" /> : <Copy className="h-4 w-4 mr-2" />}
              {linkCopied
                ? (profileUrl ? 'Profile link copied!' : 'Link copied!')
                : (profileUrl ? 'Copy profile link' : 'Copy site link')}
            </Button>
            {!isMobile && (
              <Button
                variant="outline"
                className="h-11 rounded-xl border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
                disabled={!previewUrl}
                onClick={copyImage}
              >
                <Copy className="h-4 w-4 mr-2" /> Copy image
              </Button>
            )}
            <Button
              className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#00b8ff] hover:bg-[#0099d6] text-white font-black shadow-[0_3px_0_#3a1064]"
              disabled={!previewUrl}
              onClick={shareNative}
            >
              <Share2 className="h-4 w-4 mr-2" /> Share...
            </Button>
            {!isMobile && (
              <Button
                className="h-11 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064]"
                disabled={!previewUrl}
                onClick={download}
              >
                <Download className="h-4 w-4 mr-2" /> Download PNG
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden poster rendered off-screen for capture */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: -99999,
          top: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
      >
        <SharePoster
          ref={posterRef}
          collection={collection}
          sprites={sprites}
          dataUrlMap={dataUrlMap}
          siteUrl={shortUrl}
          themeId={themeId}
          filterStatus={filterStatus}
          season={season}
          profileUrl={profileUrl}
          username={user?.username || null}
          headerImageUrl={headerDataUrl}
        />
      </div>
    </>
  );
}
